import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormattedMessage, injectIntl, intlFormatPropTypes } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import {
  DatePickerField,
  type DatePickerFieldFormatValue,
} from "@ps-design-system/components/DatePicker";
import { Divider } from "@ps-design-system/components/Divider";
import { Form } from "@ps-design-system/components/Form";
import { InputField } from "@ps-design-system/components/Input";
import { Text } from "@ps-design-system/components/Text";

import { type DateRateFormValues, dateRateSchema } from "./DateRateForm.schema";
import {
  formatDateSelectionRanges,
  getCurrencySymbol,
  getDateRateCalendarProps,
} from "./DateRateForm.utils";
import { DateRateFormLeaveDialog } from "./DateRateFormLeaveDialog";

type DateRateFormProps = {
  currency: string;
  intl: intlFormatPropTypes;
  onCancel: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  onSubmit: (dateRate: DateRateFormValues) => Promise<void> | void;
};

/** Form to add a rate that applies to a specific date or date range. */
const DateRateForm = ({
  currency,
  intl,
  onCancel,
  onDirtyChange,
  onSubmit,
}: DateRateFormProps) => {
  const form = useForm<DateRateFormValues>({
    resolver: zodResolver(dateRateSchema(intl)),
    mode: "onChange",
    defaultValues: { dates: [], hourlyRate: "" },
  });
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const { isDirty, isSubmitting } = form.formState;
  const currencySymbol = getCurrencySymbol(intl.locale, currency);
  const formatPickedDates: DatePickerFieldFormatValue = (pickedDates) =>
    formatDateSelectionRanges(
      Array.isArray(pickedDates) ? pickedDates : [],
      intl.locale,
    );

  useEffect(() => {
    onDirtyChange(isDirty);
    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  const handleCancelClick = () => {
    if (isDirty) {
      setIsLeaveDialogOpen(true);
      return;
    }
    onCancel();
  };
  const handleBackToEdit = () => setIsLeaveDialogOpen(false);

  return (
    <>
      <Form
        {...form}
        className="tw-flex tw-flex-col tw-gap-32 tw-p-16 tw-border tw-border-gray-400 tw-rounded-4"
        onSubmit={form.handleSubmit(onSubmit)}
        spacing={0}
        testId="date-rate-form"
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Text element="h4" typeface="headline-3-m" className="tw-m-0">
            <FormattedMessage
              id="hosting.date_rate_form.heading"
              defaultMessage="New rate"
            />
          </Text>
          <Text element="p" className="tw-m-0 tw-leading-28 tw-text-gray-600">
            <FormattedMessage
              id="hosting.date_rate_form.description"
              defaultMessage="This rate will replace any custom rates on these dates. It only applies to new bookings."
            />
          </Text>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-24 sm:tw-flex-row sm:tw-gap-32">
          <DatePickerField
            calendarProps={getDateRateCalendarProps()}
            className="tw-min-w-0"
            formatValue={formatPickedDates}
            fullWidth
            label={
              <FormattedMessage
                id="hosting.date_rate_form.date_label"
                defaultMessage="Date"
              />
            }
            name="dates"
            placeholder={
              <FormattedMessage
                id="hosting.date_rate_form.date_placeholder"
                defaultMessage="Select date(s)"
              />
            }
            testId="date-rate-form-date"
          />
          <InputField
            fullWidth
            inputMode="decimal"
            label={
              <FormattedMessage
                id="hosting.date_rate_form.hourly_rate_label"
                defaultMessage="Hourly rate"
              />
            }
            min={0}
            name="hourlyRate"
            prefix={currencySymbol}
            testId="date-rate-form-hourly-rate"
            type="number"
          />
        </div>
        <div className="tw-flex tw-flex-col tw-items-end tw-gap-24">
          <Divider spacing={0} />
          <div className="tw-flex tw-gap-16">
            <Button
              appearance="white"
              onClick={handleCancelClick}
              testId="date-rate-form-cancel"
              variant="outline-1"
            >
              <FormattedMessage
                id="hosting.date_rate_form.cancel_button"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              appearance="purple"
              showSpinner={isSubmitting}
              testId="date-rate-form-save"
              type="submit"
            >
              <FormattedMessage
                id="hosting.date_rate_form.save_button"
                defaultMessage="Save"
              />
            </Button>
          </div>
        </div>
      </Form>
      <DateRateFormLeaveDialog
        onBackToEdit={handleBackToEdit}
        onLeave={onCancel}
        open={isLeaveDialogOpen}
      />
    </>
  );
};

const DateRateFormWithIntl = injectIntl(DateRateForm);

export { DateRateFormWithIntl as DateRateForm };
